/**
 * Ryan O'Dowd
 * 2022-04-01
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  GOAL_ROUTE,
  apiDeleteGoal,
  apiSaveGoal,
  apiUpdateGoal,
  fetchWrinkles,
} from '@utils/store/actions';
import {
  Keyboard,
  TouchableOpacity,
  View,
} from 'react-native';
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import Dialog from '@components/common/Dialog';
import DraggableFlatList from 'react-native-draggable-flatlist';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import Icon from '@react-native-vector-icons/material-design-icons';
import IconButton from '@components/common/IconButton';
import Menu from '@components/common/Menu';
import OfflineBanner from '@components/common/OfflineBanner';
import PassageSelection from '@components/PassageSelection';
import ScreenWrapper from '@containers/ScreenWrapper';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import getStyles from './styles';
import hooks from '@hooks';
import {
  useNetInfo,
} from '@react-native-community/netinfo';

const NewGoal = () => { // @TODO: rename to something like NewEditGoal?
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const locationState = hooks.useLocationState();
  const styles = hooks.useStyles(getStyles);
  const netInfo = useNetInfo();

  hooks.useBackButton(() => {
    return false;
  });

  const goal = hooks.useTypedSelector((state) => locationState.goalId ? state.goals[locationState.goalId] : null);
  const numberOfGoals = hooks.useTypedSelector((state) => Object.keys(state.goals).length);
  const currentGoalNames = hooks.useTypedSelector((state) => Object.values(state.goals).map((g: any) => `${g.title}`), (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const updateGoalLoading = hooks.useTypedSelector((state: any) => state.loadingStates.apiUpdateGoal);
  const createGoalLoading = hooks.useTypedSelector((state: any) => state.loadingStates.apiSaveGoal);

  const [_isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const [_titleText, setTitleText] = useState(goal ? `${goal.title}` : '');
  const [_updateDisabledMessage, setUpdateDisabledMessage] = useState('');
  const [_isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [_isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [_isDeleteGoalDialogOpen, setIsDeleteGoalDialogOpen] = useState(false);
  const [_dialogInfo, setDialogInfo] = useState(null);
  const [_isPassageSelectionOpen, setIsPassageSelectionOpen] = useState(false);

  const initialPassages = hooks.useTypedSelector((state) => goal?.modules.flatMap((module) => {
    return module.assignments.map((assignment) => {
      const passageInRedux = state.passages[assignment.assignment_object.passage?.id];
      if (!passageInRedux) {
        // this should only happen on update...race condition before redux is updated with goals *and* passages
        return null;
      }
      return CPassage.createFromObject(passageInRedux);
    }).filter((passage) => passage);
  }) || [], (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));
  const [_passageOrderOverrides, setPassageOrderOverrides] = useState(initialPassages.concat(locationState.initialPassages || []));

  useEffect(() => {
    if (updateGoalLoading) {
      setUpdateDisabledMessage(i18n('updateDisabledUpdateInProgress'));
    } else if (createGoalLoading) {
      setUpdateDisabledMessage(i18n('saveDisabledSaveInProgress'));
    } else {
      setUpdateDisabledMessage('');
    }
  }, [i18n, updateGoalLoading, createGoalLoading]);

  const books = hooks.useTypedSelector((state) => state.books);
  const translations = hooks.useTypedSelector((state) => state.translations);

  const backIcon = (
    <Header.Icon
      icon='close'
      onPress={() => {
        if (
          (!goal && (_titleText || _passageOrderOverrides.length)) || // new goal with a title or at least one passage selected
          (goal && // updating goal
            ((goal.title !== _titleText) || // title updated
             ((_passageOrderOverrides.map((p) => p.id).join(',') !== initialPassages.map((p) => p.id).join(',')))))) { // passage(s) added, removed, or reordered
          setIsCancelDialogOpen(true);
        } else {
          navigation.goBack();
        }
      }}
    />
  );

  const menuItem = !goal ? null : (
    <Menu
      statusBarHeight={styles.safeAreaInsets.top}// @TODO: abstract menu to oak component and abstract this prop away
      anchor={
        <Header.Icon
          icon='dots-vertical'
          onPress={() => setIsOverflowMenuOpen(true)}
        />
      }
      visible={_isOverflowMenuOpen}
      onDismiss={() => setIsOverflowMenuOpen(false)}
    >
      {goal.user_goal.status === 'archived'
        ? (
          <Menu.Item
            title={i18n('restoreGoal')}
            titleStyle={styles.archiveText}
            onPress={() => {
              dispatch(apiUpdateGoal(goal.id, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, 'active', undefined));
              setIsOverflowMenuOpen(false);
            }}
          />
        )
        : (
          <Menu.Item
            title={i18n('archiveGoal')}
            titleStyle={styles.archiveText}
            onPress={() => {
              setIsOverflowMenuOpen(false);
              setIsArchiveDialogOpen(true);
            }}
          />
        )
      }
      {!goal?.classroom_goal_metadata.classroom_id &&
        <Menu.Item
          title={i18n('delete')}
          titleStyle={styles.deleteText}
          onPress={() => {
            setIsOverflowMenuOpen(false);
            setIsDeleteGoalDialogOpen(true);
          }}
        />
      }
    </Menu>
  );

  const passagesVerses = hooks.useGetVersesForPassages(_passageOrderOverrides);
  const _renderPassage = useCallback((d) => { // @TODO: this should be its own component
    // @TODO: convert to use @components/Passage; add prop to it for displaying verses/words and translation abbreviation (all three of these are helpful for schools in the goal builder as well!)
    const passage = d.item;
    // @TODO: should numWordsInPassage be in database? would save a lot of front-end calculations
    const passageVerses = passagesVerses[passage.id] || [];
    const numWordsInPassage = passageVerses.map((pv) => pv.verseText).join(' ').split(' ').filter((x) => x).length;

    return (
      <View key={passage.id} style={styles.passageContainer}>
        {!goal?.classroom_goal_metadata.classroom_id &&
          <TouchableOpacity
            style={styles.dragWrapper}
            onPressIn={d.drag}
          >
            <Icon style={styles.dragIcon} name='drag' />
          </TouchableOpacity>
        }
        <View style={styles.passageWrapper}>
          <View style={styles.row}>
            <Text style={styles.passageText}>{books.find((b) => b.id === passage.bookId)?.name} {passage.chapterNumber}</Text>
            {!(+passage.startVerseNumber === 1 && +passage.endVerseNumber === 50) && // @TODO: should be number verses in this chapter, not 50...fix this this deployment
              <React.Fragment>
                <Text style={styles.passageText}>{`:${passage.startVerseNumber}`}</Text>
                {(passage.startVerseNumber !== passage.endVerseNumber) &&
                  <Text style={styles.passageText}>{`-${passage.endVerseNumber}`}</Text>
                }
              </React.Fragment>
            }
            <Text style={styles.translationText}>{translations.find((t) => t.id === passage.translationId)?.abbreviation}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.metadata}>{passage.endVerseNumber - passage.startVerseNumber + 1} {passage.startVerseNumber === passage.endVerseNumber ? i18n('verseLowercase') : i18n('versesLowercase')}, {numWordsInPassage} {i18n('wordsLowercase')}</Text>
          </View>
          {!goal?.classroom_goal_metadata.classroom_id &&
            <View style={styles.actionsWrapper}>
              <IconButton
                style={styles.deleteButton}
                icon='close'
                iconColor={styles.colors.accent}
                size={22}
                onPress={() => {
                  // @TODO: confirmation on delete (reassure that peanuts/wrinkles stay in account, but warn that progress in this passage will not be visible in this goal)
                  setPassageOrderOverrides((curr) => curr.filter((p) => p.id !== passage.id));
                }}
              />
            </View>
          }
        </View>
      </View>
    );
  }, [passagesVerses, books, i18n, translations, styles, goal?.classroom_goal_metadata.classroom_id]);

  const rightItems = (goal && numberOfGoals > 1) ? [menuItem] : [];

  return (
    <ScreenWrapper>
      <Header
        title={goal ? i18n('updateGoal') : i18n('createNewGoal')}
        leftItems={[backIcon]}
        rightItems={rightItems}
      />
      <FullScreenScrollView>
        <View style={styles.innerContainer}>
          {!!goal?.classroom_goal_metadata.classroom_id &&
            <View style={styles.formSection}>
              <View style={styles.noteWrapper}>
                <Text style={styles.noteText}>{i18n('cantEditGoalExplanation')}</Text>
              </View>
            </View>
          }
          <View style={styles.formSection}>
            <Text style={styles.sectionHeaderText}>{i18n('title')}</Text>
            <View style={styles.formSectionInner}>
              <TextInput
                style={styles.titleTextInput}
                textColor={styles.colors.white}
                // @TODO: does this work on native and web? inputStyleProps={styles.titleTextInput}
                // @TODO: why isn't this selectionColor accent? that's set in the common component, and none of these should override that
                disabled={goal?.classroom_goal_metadata.classroom_id}
                maxLength={128}
                autoFocus={false}
                autoCapitalize='sentences'
                autoCorrect={true}
                onChangeText={(titleText) => setTitleText(titleText)}
                value={_titleText}
                multiline={true}
                placeholder={i18n('nameOfGoal')}
                activeUnderlineColor={styles.colors.accent}
                underlineColor={styles.isDarkMode ? styles.colors.primaryLight : styles.colors.primaryDark}
              />
            </View>
          </View>
          <View style={[styles.formSection, styles.passagesFormSection]}>{/* @TODO: native flex grow not working...finish should be at bottom of screen */}
            <View style={styles.titleAndButton}>
              <Text style={styles.sectionHeaderText}>{i18n('passages')}</Text>
              {!goal?.classroom_goal_metadata.classroom_id &&
                <Button
                  style={styles.addPassageButton}
                  contentStyle={styles.addPassageButtonContent}
                  labelStyle={styles.addPassageButtonText}
                  mode='contained'
                  icon='plus'
                  theme={styles.colors.accent}
                  onPress={() => {
                    Keyboard.dismiss();
                    setIsPassageSelectionOpen(true);
                  }}
                >
                  {i18n('addPassage')}
                </Button>
              }
            </View>
            <View style={[styles.formSectionInner, styles.passagesFormSectionInner]}>
              <DraggableFlatList
                keyExtractor={(p: any) => p.id}
                style={styles.list}
                data={_passageOrderOverrides}
                activationDistance={1}// @NOTE: needs to be here for web to work
                showsVerticalScrollIndicator={false}
                renderItem={_renderPassage}
                keyboardDismissMode='on-drag'
                keyboardShouldPersistTaps='handled'
                scrollEnabled={false} // @TODO: should this be true if it's a long goal?
                onDragEnd={({data}) => {
                  // @NOTE: temporarily (component state) set note order so that the delay caused by writing to firebase, updating redux, and rerendering doesn't cause a flicker where the dragged note goes back to its original place and then jumps to the new location
                  setPassageOrderOverrides(data);
                }}
              />
            </View>
          </View>
          {!goal?.classroom_goal_metadata.classroom_id &&
            <View style={styles.formSection}>
              <Text style={styles.sectionHeaderText}>{i18n('finish')}</Text>
              <View style={styles.formSectionInner}>
                <OfflineBanner containerStyle={styles.offlineBanner} message={i18n('offlineTryAgain')} />
                <Button
                  containerStyle={styles.submitButton}
                  mode='contained'
                  disabled={!netInfo.isInternetReachable || !!_updateDisabledMessage}
                  // @TODO: add loading prop for while goal is saving to avoid double press
                  theme={styles.colors.accent}
                  onPress={() => {
                    if (!_titleText) {
                      setDialogInfo({
                        title: i18n('missingTitle'),
                        bodyText: i18n('missingTitleMessage'),
                      });
                    } else if (currentGoalNames.includes(_titleText) && (goal && `${goal.title}` !== _titleText)) {
                      setDialogInfo({
                        title: i18n('nameInUse'),
                        bodyText: i18n('nameInUseMessage', _titleText),
                      });
                    } else if (!_passageOrderOverrides.filter((p) => CPassage.createFromObject({...p}).isValid()).length) { // @TODO: why the spread?
                      setDialogInfo({
                        title: i18n('invalidPassages'),
                        bodyText: i18n('invalidPassagesMessage'),
                      });
                    } else {
                      // save to back end
                      if (goal) {
                        // @TODO: be sure to save order of passages
                        let passageIdsToSave = _passageOrderOverrides.map((p) => p.id);
                        if (JSON.stringify(passageIdsToSave) === JSON.stringify(initialPassages.map((p) => p.id))) {
                          // passages haven't changed, so no need to send to back end (currently, it's a little expensive on the back end to deal with passages)
                          passageIdsToSave = null;
                        }
                        dispatch(apiUpdateGoal(goal.id, _titleText, undefined, undefined, undefined, passageIdsToSave, undefined, undefined, undefined, undefined, () => {
                          navigation.goBack();
                          return fetchWrinkles();
                        }));
                      } else {
                        // this is a new goal
                        dispatch(apiSaveGoal(_titleText, _passageOrderOverrides.map((p) => p.id), () => {
                          navigation.replace(GOAL_ROUTE);
                        }));
                      }
                    }
                  }}
                >
                  {goal ? i18n('updateGoal') : i18n('saveGoal')}
                </Button>
                {!!_updateDisabledMessage && <Text style={styles.updateDisabledText}>{_updateDisabledMessage}</Text>}
              </View>
            </View>
          }
        </View>
      </FullScreenScrollView>
      {_dialogInfo &&
        <Dialog
          visible={_dialogInfo}
          onDismiss={() => setDialogInfo(null)}
        >
          <Dialog.Title>{_dialogInfo.title}</Dialog.Title>
          <Dialog.Content>
            <Text>{_dialogInfo.bodyText}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='contained'
              onPress={() => setDialogInfo(null)}
            >
              {i18n('okay')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
      {_isCancelDialogOpen &&
        <Dialog
          visible={_isCancelDialogOpen}
          onDismiss={() => setIsCancelDialogOpen(false)}
        >
          <Dialog.Title>{i18n('cancelEdits')}</Dialog.Title>
          <Dialog.Content>
            <Text>{i18n('cancelEditsBody')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => setIsCancelDialogOpen(false)}
            >
              {i18n('cancelEditsContinueEditing')}{/* @TODO: shouldn't say "continue editing"...should stay "don't archive" */}
            </Button>
            <Button
              mode='contained'
              theme={styles.colors.alert}
              onPress={() => {
                navigation.goBack();
                setIsCancelDialogOpen(false);
              }}
            >
              {i18n('cancelEditsConfirm')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
      {_isArchiveDialogOpen &&
        <Dialog
          visible={_isArchiveDialogOpen}
          onDismiss={() => setIsArchiveDialogOpen(false)}
        >
          <Dialog.Title>{i18n('archiveGoalTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text>{i18n('archiveGoalBody')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => setIsArchiveDialogOpen(false)}
            >
              {i18n('deleteGoalCancel')}{/* @TODO: change name of key? this is archive */}
            </Button>
            <Button
              mode='contained'
              theme={styles.colors.alert}
              onPress={() => {
                dispatch(apiUpdateGoal(goal.id, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, 'archived', undefined));
                navigation.goBack();
                setIsArchiveDialogOpen(false);
              }}
            >
              {i18n('archiveGoalConfirm')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
      {_isDeleteGoalDialogOpen &&
        <Dialog
          visible={_isDeleteGoalDialogOpen}
          onDismiss={() => setIsDeleteGoalDialogOpen(false)}
        >
          <Dialog.Title>{i18n('deleteGoalTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text>{i18n('deleteGoalBody')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => setIsDeleteGoalDialogOpen(false)}
            >
              {i18n('deleteGoalCancel')}
            </Button>
            <Button
              mode='contained'
              theme={styles.colors.alert}
              onPress={() => {
                dispatch(apiDeleteGoal(goal.id));
                navigation.goBack();
                setIsDeleteGoalDialogOpen(false);
              }}
            >
              {i18n('deleteGoalConfirm')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
      {_isPassageSelectionOpen &&
        <PassageSelection
          onClose={() => setIsPassageSelectionOpen(false)}
          onSelectPassage={(newPassageJson) =>  {
            const newPassage = CPassage.createFromObject(newPassageJson);
            if (_passageOrderOverrides.map((p) => p.id).includes(newPassage.id)) {
              setDialogInfo({
                title: i18n('duplicatePassageTitle'),
                bodyText: i18n('duplicatePassageBody'),
              });
            } else {
              setPassageOrderOverrides((curr) => [...curr, newPassage]);
            }
            setIsPassageSelectionOpen(false);
          }}
        />
      }
    </ScreenWrapper>
  );
};

export default NewGoal;
